import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { historyService } from '../api/history.service';
import { isActiveJob } from '../model/history.types';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type {
  HistoryItem,
  HistoryFilters,
  HistorySortBy,
  HistoryResponse,
  RenderHistoryItem,
  RenderHistoryResponse,
  JobHistoryItem,
  JobHistoryResponse,
} from '../model/history.types';
import { ApiClientError } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import { cacheInvalidation } from '@/shared/lib/cache-invalidation';

// Type guards for TanStack Query cache data
type QueryData = { data: HistoryItem[] } & Record<string, unknown>;
type InfiniteQueryData = { pages: HistoryResponse[] } & Record<string, unknown>;
type RenderQueryData = { data: RenderHistoryItem[] } & Record<string, unknown>;
type RenderInfiniteQueryData = { pages: RenderHistoryResponse[] } & Record<
  string,
  unknown
>;

function isQueryData(value: unknown): value is QueryData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    Array.isArray((value as { data: unknown }).data)
  );
}

function isInfiniteQueryData(value: unknown): value is InfiniteQueryData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'pages' in value &&
    Array.isArray((value as { pages: unknown }).pages)
  );
}


/**
 * Hook for fetching paginated history
 */
export function useConceptsHistory(
  filters?: HistoryFilters,
  sort: HistorySortBy = 'date-desc',
  page = 1,
  pageSize = 20
) {
  const sortString = getSortString(sort);

  return useQuery({
    queryKey: cacheKeys.history.list(filters, sortString),
    queryFn: () =>
      historyService.getHistory({
        filter: filters,
        sort: sortString,
        page: { number: page, size: pageSize },
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes - increased for better caching
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in memory longer
  });
}

/**
 * Hook for infinite scrolling history
 */
export function useInfiniteConceptsHistory(
  filters?: HistoryFilters,
  sort: HistorySortBy = 'date-desc',
  pageSize = 20
) {
  const sortString = getSortString(sort);

  return useInfiniteQuery({
    queryKey: cacheKeys.history.infinite(filters, sortString),
    queryFn: ({ pageParam = 1 }) =>
      historyService.getHistory({
        filter: filters,
        sort: sortString,
        page: { number: pageParam, size: pageSize },
      }),
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      const totalItems = lastPage.meta?.total || 0;
      const itemsPerPage = lastPage.meta?.per_page || pageSize;
      const totalPages = Math.ceil(totalItems / itemsPerPage);

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes - increased for better caching
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in memory longer
  });
}

/**
 * Hook for toggling favorite status
 */
export function useToggleFavorite(
  filters?: HistoryFilters,
  sortString?: string
) {
  const queryClient = useQueryClient();
  const tErrors = useTranslations('common.errors');

  return useMutation({
    mutationKey: ['concepts', 'toggle-favorite'],
    meta: { lockId: (vars: unknown) => (vars as { conceptId: string }).conceptId },
    mutationFn: ({
      projectId,
      conceptId,
      newIsFavorite,
    }: {
      projectId: string;
      conceptId: string;
      newIsFavorite: boolean;
    }) => historyService.toggleFavorite(projectId, conceptId, newIsFavorite),

    onMutate: async ({ conceptId, newIsFavorite }) => {
      // Cancel outgoing queries for the specific filters
      const specificKeys: ReadonlyArray<readonly unknown[]> = [
        cacheKeys.history.lists(),
        ...(filters !== undefined
          ? [cacheKeys.history.infinite(filters, sortString)]
          : []),
      ];

      await Promise.all(
        specificKeys.map((key) => queryClient.cancelQueries({ queryKey: key }))
      );

      // Store previous data for rollback
      const previousData: Array<[readonly unknown[], unknown]> = [];

      // Optimistically update specific cached queries
      specificKeys.forEach((queryKey) => {
        const oldData = queryClient.getQueryData(queryKey);
        if (oldData) {
          previousData.push([queryKey, oldData]);

          queryClient.setQueryData(queryKey, (old: unknown) => {
            if (!old) return old;

            // Handle regular query data
            if (isQueryData(old)) {
              return {
                ...old,
                data: old.data.map((item: HistoryItem) =>
                  item.id === conceptId
                    ? {
                        ...item,
                        attributes: {
                          ...item.attributes,
                          is_favorite: newIsFavorite,
                        },
                      }
                    : item
                ),
              };
            }

            // Handle infinite query data
            if (isInfiniteQueryData(old)) {
              return {
                ...old,
                pages: old.pages.map((page: HistoryResponse) => ({
                  ...page,
                  data: page.data.map((item: HistoryItem) =>
                    item.id === conceptId
                      ? {
                          ...item,
                          attributes: {
                            ...item.attributes,
                            is_favorite: newIsFavorite,
                          },
                        }
                      : item
                  ),
                })),
              };
            }

            return old;
          });
        }
      });

      return { previousData };
    },

    onError: (err, variables, context) => {
      // Revert on error
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error(
        err instanceof ApiClientError ? err.getFirstError() : tErrors('generic')
      );
    },

    onSettled: (_data, _error, variables) => {
      // Refetch specific queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: cacheKeys.history.lists() });
      // Invalidate concepts list/detail so overview page picks up the change
      cacheInvalidation.afterUpdateConcept(
        variables.conceptId,
        variables.projectId
      );
    },
  });
}

/**
 * Hook for deleting a concept
 */
export function useDeleteConcept() {
  const queryClient = useQueryClient();
  const tErrors = useTranslations('common.errors');

  return useMutation({
    mutationKey: ['concepts', 'delete'],
    meta: { lockId: (vars: unknown) => (vars as { conceptId: string }).conceptId },
    mutationFn: ({
      projectId,
      conceptId,
    }: {
      projectId: string;
      conceptId: string;
    }) => historyService.deleteConcept(projectId, conceptId),

    onSuccess: (_, { conceptId }) => {
      // Remove from cache
      queryClient.setQueriesData(
        { queryKey: cacheKeys.history.all },
        (oldData: unknown) => {
          if (!oldData) return oldData;

          // Handle regular query data
          if (isQueryData(oldData)) {
            return {
              ...oldData,
              data: oldData.data.filter(
                (item: HistoryItem) => item.id !== conceptId
              ),
            };
          }

          // Handle infinite query data
          if (isInfiniteQueryData(oldData)) {
            return {
              ...oldData,
              pages: oldData.pages.map((page: HistoryResponse) => ({
                ...page,
                data: page.data.filter(
                  (item: HistoryItem) => item.id !== conceptId
                ),
              })),
            };
          }

          return oldData;
        }
      );

    },

    onError: (error) => {
      toast.error(
        error instanceof ApiClientError ? error.getFirstError() : tErrors('generic')
      );
    },

    onSettled: () => {
      // Refetch specific queries to ensure consistency
      queryClient.invalidateQueries({ queryKey: cacheKeys.history.lists() });
      // Note: filters and sortString should be passed from the component if needed
      // to enable more targeted cache invalidation
    },
  });
}

/**
 * Convert sort type to API sort string
 */
export function getSortString(sort: HistorySortBy): string {
  switch (sort) {
    case 'date-desc':
      return '-created_at';
    case 'date-asc':
      return 'created_at';
    case 'name-asc':
      return 'name';
    case 'name-desc':
      return '-name';
    default:
      return '-created_at';
  }
}

// ============================================================================
// RENDER HISTORY HOOKS (for dashboard and history screen)
// ============================================================================

// Type guards for render history
function isRenderQueryData(value: unknown): value is RenderQueryData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    Array.isArray((value as { data: unknown }).data)
  );
}

function isRenderInfiniteQueryData(
  value: unknown
): value is RenderInfiniteQueryData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'pages' in value &&
    Array.isArray((value as { pages: unknown }).pages)
  );
}


/**
 * Hook for fetching paginated render history
 */
export function useRenderHistory(
  filters?: HistoryFilters,
  sort: HistorySortBy = 'date-desc',
  page = 1,
  pageSize = 20
) {
  const sortString = getSortString(sort);

  return useQuery({
    queryKey: cacheKeys.renderHistory.list(filters, sortString),
    queryFn: () =>
      historyService.getRenderHistory({
        filter: filters,
        sort: sortString,
        page: { number: page, size: pageSize },
      }),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Hook for infinite scrolling render history
 */
export function useInfiniteRenderHistory(
  filters?: HistoryFilters,
  sort: HistorySortBy = 'date-desc',
  pageSize = 20
) {
  const sortString = getSortString(sort);

  return useInfiniteQuery({
    queryKey: cacheKeys.renderHistory.infinite(filters, sortString),
    queryFn: ({ pageParam = 1 }) =>
      historyService.getRenderHistory({
        filter: filters,
        sort: sortString,
        page: { number: pageParam, size: pageSize },
      }),
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      const totalItems = lastPage.meta?.total || 0;
      const itemsPerPage = lastPage.meta?.per_page || pageSize;
      const totalPages = Math.ceil(totalItems / itemsPerPage);

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Hook for toggling favorite status (works on concept level)
 */
export function useToggleRenderFavorite(
  filters?: HistoryFilters,
  sortString?: string
) {
  const queryClient = useQueryClient();
  const tErrors = useTranslations('common.errors');

  return useMutation({
    mutationKey: ['concepts', 'toggle-favorite'],
    meta: { lockId: (vars: unknown) => (vars as { conceptId: string }).conceptId },
    mutationFn: ({
      projectId,
      conceptId,
      newIsFavorite,
    }: {
      projectId: string;
      conceptId: string;
      newIsFavorite: boolean;
    }) => historyService.toggleFavorite(projectId, conceptId, newIsFavorite),

    onError: (error) => {
      toast.error(
        error instanceof ApiClientError ? error.getFirstError() : tErrors('generic')
      );
    },

    onSettled: () => {
      // Invalidate render history queries
      queryClient.invalidateQueries({ queryKey: cacheKeys.renderHistory.lists() });
      if (filters !== undefined) {
        queryClient.invalidateQueries({
          queryKey: cacheKeys.renderHistory.infinite(filters, sortString),
        });
      }
    },
  });
}

/**
 * Hook for deleting a render
 */
export function useDeleteRender() {
  const queryClient = useQueryClient();
  const tErrors = useTranslations('common.errors');

  return useMutation({
    mutationKey: ['renders', 'delete'],
    meta: { lockId: (vars: unknown) => (vars as { renderId: string }).renderId },
    mutationFn: ({ renderId }: { renderId: string }) =>
      historyService.deleteRender(renderId),

    onSuccess: (_, { renderId }) => {
      // Remove from cache
      queryClient.setQueriesData(
        { queryKey: cacheKeys.renderHistory.all },
        (oldData: unknown) => {
          if (!oldData) return oldData;

          if (isRenderQueryData(oldData)) {
            return {
              ...oldData,
              data: oldData.data.filter(
                (item: RenderHistoryItem) => item.id !== renderId
              ),
            };
          }

          if (isRenderInfiniteQueryData(oldData)) {
            return {
              ...oldData,
              pages: oldData.pages.map((page: RenderHistoryResponse) => ({
                ...page,
                data: page.data.filter(
                  (item: RenderHistoryItem) => item.id !== renderId
                ),
              })),
            };
          }

          return oldData;
        }
      );

    },

    onError: (error) => {
      toast.error(
        error instanceof ApiClientError ? error.getFirstError() : tErrors('generic')
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cacheKeys.renderHistory.lists() });
    },
  });
}

// ============================================================================
// JOB HISTORY HOOKS (renders + albums combined)
// ============================================================================

// Type guards for job history
type JobQueryData = { data: JobHistoryItem[] } & Record<string, unknown>;
type JobInfiniteQueryData = { pages: JobHistoryResponse[] } & Record<
  string,
  unknown
>;

function isJobQueryData(value: unknown): value is JobQueryData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    Array.isArray((value as { data: unknown }).data)
  );
}

function isJobInfiniteQueryData(value: unknown): value is JobInfiniteQueryData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'pages' in value &&
    Array.isArray((value as { pages: unknown }).pages)
  );
}


/**
 * Hook for fetching paginated job history (renders + albums)
 */
export function useJobHistory(
  filters?: HistoryFilters,
  sort: HistorySortBy = 'date-desc',
  page = 1,
  pageSize = 20
) {
  const sortString = getSortString(sort);

  return useQuery({
    queryKey: cacheKeys.jobHistory.list(filters, sortString),
    queryFn: () =>
      historyService.getJobsHistory({
        filter: filters,
        sort: sortString,
        page: { number: page, size: pageSize },
      }),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Hook for infinite scrolling job history (renders + albums)
 */
export function useInfiniteJobHistory(
  filters?: HistoryFilters,
  sort: HistorySortBy = 'date-desc',
  pageSize = 20
) {
  const sortString = getSortString(sort);

  return useInfiniteQuery({
    queryKey: cacheKeys.jobHistory.infinite(filters, sortString),
    queryFn: ({ pageParam = 1 }) =>
      historyService.getJobsHistory({
        filter: filters,
        sort: sortString,
        page: { number: pageParam, size: pageSize },
      }),
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      const totalItems = lastPage.meta?.total || 0;
      const itemsPerPage = lastPage.meta?.per_page || pageSize;
      const totalPages = Math.ceil(totalItems / itemsPerPage);

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Hook for deleting a job item (render or album)
 */
export function useDeleteJobItem() {
  const queryClient = useQueryClient();
  const tErrors = useTranslations('common.errors');

  // Note: lock entity is intentionally `renders` only — albums have no
  // delete endpoint today, so the album path always throws and never
  // races with a WS event. When album deletion ships, this can split into
  // two mutations or use `meta.lockEntity` once that mechanism exists.
  return useMutation({
    mutationKey: ['renders', 'delete'],
    meta: {
      lockId: (vars: unknown) => {
        const { itemId, itemType } = vars as {
          itemId: string;
          itemType: 'job-render' | 'job-album';
        };
        return itemType === 'job-render' ? itemId : undefined;
      },
    },
    mutationFn: async ({
      itemId,
      itemType,
    }: {
      itemId: string;
      itemType: 'job-render' | 'job-album';
    }) => {
      if (itemType === 'job-render') {
        return historyService.deleteRender(itemId);
      }
      // Albums don't have a delete endpoint yet - skip for now
      throw new Error('Album deletion not implemented');
    },

    onSuccess: (_, { itemId }) => {
      // Remove from cache
      queryClient.setQueriesData(
        { queryKey: cacheKeys.jobHistory.all },
        (oldData: unknown) => {
          if (!oldData) return oldData;

          if (isJobQueryData(oldData)) {
            return {
              ...oldData,
              data: oldData.data.filter(
                (item: JobHistoryItem) => item.id !== itemId
              ),
            };
          }

          if (isJobInfiniteQueryData(oldData)) {
            return {
              ...oldData,
              pages: oldData.pages.map((page: JobHistoryResponse) => ({
                ...page,
                data: page.data.filter(
                  (item: JobHistoryItem) => item.id !== itemId
                ),
              })),
            };
          }

          return oldData;
        }
      );

    },

    onError: (error) => {
      toast.error(
        error instanceof ApiClientError ? error.getFirstError() : tErrors('generic')
      );
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.lists() });
    },
  });
}

// ============================================================================
// ACTIVE JOBS HOOKS (for job queue indicator)
// ============================================================================

/**
 * Hook for fetching active jobs (pending/processing) for the job queue indicator.
 * Uses the same job history endpoint but filters client-side for simplicity.
 *
 * Note: Backend already sorts PENDING/PROCESSING first, so we just need to filter.
 */
export function useActiveJobs(pageSize = 20) {
  const { data, isLoading, error, refetch } = useJobHistory(
    undefined, // no filters - get all jobs including active ones
    'date-desc',
    1,
    pageSize
  );

  const activeJobs = useMemo(() => {
    if (!data?.data) return [];
    return data.data.filter(isActiveJob);
  }, [data?.data]);

  const completedJobs = useMemo(() => {
    if (!data?.data) return [];
    return data.data.filter((job) => !isActiveJob(job));
  }, [data?.data]);

  return {
    activeJobs,
    completedJobs,
    allJobs: data?.data || [],
    activeCount: activeJobs.length,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Helper to invalidate job history queries (used by WebSocket handlers)
 */
export function invalidateJobHistory(
  queryClient: ReturnType<typeof useQueryClient>
) {
  queryClient.invalidateQueries({ queryKey: cacheKeys.jobHistory.all });
}
